'use client';
import React, { useState, useEffect } from 'react';
import {
  Ship,
  MapPin,
  Calendar,
  RefreshCw,
  Bell,
  Package,
  Clock,
  CheckCircle2,
  Circle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  FileCheck,
  FileText,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface ContainerData {
  id: string;
  number: string;
  type: string;
  weight: string;
  status: string;
  seal: string;
}

interface EventData {
  id: string;
  date: string;
  time: string;
  location: string;
  event: string;
  status: string;
  current: boolean;
}

interface BLData {
  blNumber: string;
  carrier: string;
  carrierCode: string;
  vessel: string;
  voyage: string;
  origin: { port: string; code: string; country: string };
  destination: { port: string; code: string; country: string };
  status: string;
  eta: string;
  etd: string;
  lastUpdate: string;
  containers: ContainerData[];
  events: EventData[];
}

interface Props {
  data: BLData;
  refreshing: boolean;
  onRefresh: () => void;
  onSetAlert: () => void;
}

const DOCUMENT_LIST = [
  { id: 'doc-bl', label: 'Bill of Lading (BL)', description: 'Original or Telex Release BL from carrier' },
  { id: 'doc-invoice', label: 'Commercial Invoice', description: 'Invoice from exporter with value & description' },
  { id: 'doc-packing', label: 'Packing List', description: 'Detailed list of goods, weights & dimensions' },
  { id: 'doc-nfmims', label: 'NFMIMS', description: 'Non-Food Mandatory Import Management System certificate' },
  { id: 'doc-coo', label: 'Certificate of Origin', description: 'Country of origin certificate (COO)' },
  { id: 'doc-marine', label: 'Marine Insurance', description: 'Marine cargo insurance policy / certificate' },
  { id: 'doc-coa', label: 'Certificate of Analysis', description: 'Lab analysis / quality certificate for goods' },
];

export default function BLResultPanel({ data, refreshing, onRefresh, onSetAlert }: Props) {
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [checkedDocs, setCheckedDocs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setCountdown(120);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Backend integration point: auto-refresh trigger — re-fetch carrier API
          onRefresh();
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [data.blNumber]);

  const visibleEvents = showAllEvents ? data.events : data.events.slice(0, 3);

  const toggleDoc = (id: string) => {
    setCheckedDocs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const checkedCount = Object.values(checkedDocs).filter(Boolean).length;

  return (
    <div className="slide-up flex flex-col gap-4">
      {/* Header card */}
      <div className="card-surface p-5 shadow-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl gradient-ocean flex items-center justify-center shrink-0">
              <Ship size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-foreground font-tabular">{data.blNumber}</h2>
                <StatusBadge status={data.status} size="md" />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {data.carrier} · {data.vessel} · Voyage {data.voyage}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Auto-refresh countdown */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">
              <div className="pulse-dot" />
              <span>
                {refreshing ? 'Refreshing...' : `Auto-refresh in ${countdown}s`}
              </span>
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
              title="Refresh now"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onSetAlert}
              className="btn-accent flex items-center gap-2 px-4 py-2 text-sm"
            >
              <Bell size={15} />
              Set Alert
            </button>
          </div>
        </div>

        {/* Route bar */}
        <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-0">
          <div className="flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Origin</p>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">{data.origin.port}</p>
                <p className="text-xs text-muted-foreground">{data.origin.code} · ETD {data.etd}</p>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex flex-1 items-center justify-center px-4">
            <div className="flex-1 h-px bg-border" />
            <div className="mx-3 flex flex-col items-center">
              <Ship size={18} className="text-accent" />
              <span className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                {data.carrier}
              </span>
            </div>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="flex-1 sm:text-right">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Destination</p>
            <div className="flex items-center gap-2 sm:justify-end">
              <MapPin size={14} className="text-success shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">{data.destination.port}</p>
                <p className="text-xs text-muted-foreground">{data.destination.code} · ETA {data.eta}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar size={12} />
            <span>ETA: <span className="font-semibold text-foreground">{data.eta}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={12} />
            <span>Last updated: <span className="font-semibold text-foreground">{data.lastUpdate}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <Package size={12} />
            <span>{data.containers.length} Container{data.containers.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Document Checklist — placed right after header for immediate visibility */}
      <div className="card-surface p-5 shadow-card border-l-4 border-l-primary">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <FileCheck size={16} className="text-primary" />
            Document Checklist
          </h3>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            checkedCount === DOCUMENT_LIST.length
              ? 'bg-success/10 text-success'
              : checkedCount > 0
              ? 'bg-warning/10 text-warning' :'bg-muted text-muted-foreground'
          }`}>
            {checkedCount} / {DOCUMENT_LIST.length} Documents Ready
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Tick the documents you currently have in hand for this shipment.
        </p>

        {/* Progress bar */}
        <div className="w-full h-2 bg-muted rounded-full mb-5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${(checkedCount / DOCUMENT_LIST.length) * 100}%`,
              background: checkedCount === DOCUMENT_LIST.length
                ? 'var(--success)'
                : checkedCount > 0
                ? 'var(--warning)'
                : 'var(--muted-foreground)',
            }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {DOCUMENT_LIST.map((doc) => {
            const isChecked = !!checkedDocs[doc.id];
            return (
              <button
                key={doc.id}
                onClick={() => toggleDoc(doc.id)}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                  isChecked
                    ? 'border-success bg-success/5 shadow-sm'
                    : 'border-border bg-background hover:border-primary/40 hover:bg-muted/50'
                }`}
              >
                <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 transition-all ${
                  isChecked ? 'border-success bg-success' : 'border-muted-foreground'
                }`}>
                  {isChecked && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold leading-tight ${isChecked ? 'text-success' : 'text-foreground'}`}>
                    {doc.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-tight line-clamp-2">
                    {doc.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {checkedCount === DOCUMENT_LIST.length && (
          <div className="mt-4 flex items-center gap-2 bg-success/10 border border-success/30 rounded-lg px-4 py-3">
            <CheckCircle2 size={16} className="text-success shrink-0" />
            <p className="text-sm font-semibold text-success">All documents are in hand — shipment is ready for clearance!</p>
          </div>
        )}
        {checkedCount > 0 && checkedCount < DOCUMENT_LIST.length && (
          <div className="mt-4 flex items-center gap-2 bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
            <FileText size={16} className="text-warning shrink-0" />
            <p className="text-sm font-medium text-warning">
              <span className="font-bold">{DOCUMENT_LIST.length - checkedCount} document{DOCUMENT_LIST.length - checkedCount > 1 ? 's' : ''}</span> still pending — please arrange before vessel arrival.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Containers */}
        <div className="card-surface p-5 shadow-card">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Package size={16} className="text-primary" />
            Container Details
          </h3>
          <div className="flex flex-col gap-3">
            {data.containers.map((container) => (
              <div
                key={container.id}
                className="rounded-lg bg-muted p-4 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-foreground font-tabular">
                    {container.number}
                  </span>
                  <StatusBadge status={container.status} size="xs" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-semibold text-foreground">{container.type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Weight</p>
                    <p className="font-semibold text-foreground">{container.weight}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Seal No.</p>
                    <p className="font-semibold text-foreground font-tabular">{container.seal}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Event Timeline */}
        <div className="card-surface p-5 shadow-card">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Clock size={16} className="text-primary" />
            Shipment Timeline
          </h3>
          <div className="relative">
            <div className="absolute left-4 top-2 bottom-2 timeline-line" />
            <div className="flex flex-col gap-0">
              {visibleEvents.map((event) => (
                <div key={event.id} className="flex gap-3 relative pb-4 last:pb-0">
                  <div className="shrink-0 w-8 flex justify-center z-10">
                    {event.current ? (
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shadow-md">
                        <CheckCircle2 size={16} className="text-white" />
                      </div>
                    ) : event.status === 'Customs Hold' ? (
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <AlertTriangle size={14} className="text-warning" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-muted border-2 border-border flex items-center justify-center">
                        <Circle size={12} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className={`text-sm font-semibold ${event.current ? 'text-accent' : 'text-foreground'}`}>
                      {event.event}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <MapPin size={10} />
                      {event.location}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {event.date} · {event.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {data.events.length > 3 && (
            <button
              onClick={() => setShowAllEvents(!showAllEvents)}
              className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-accent hover:text-primary transition-colors"
            >
              {showAllEvents ? (
                <><ChevronUp size={14} /> Show less</>
              ) : (
                <><ChevronDown size={14} /> Show all {data.events.length} events</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}